import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import axiosApexInstance from '../auth/axiosApexInstance';
import { CPTItem, ApexProcedureItem, CodeListItem } from '../types';
import {EcwDocFolder} from "../components/patient/SelectPtDocFolder";
import {MISSING_TOKEN_ERROR_MSG} from "../Constants";

interface DictCptEntry {
    code: string;
    description: string;
    freqcount: number;
}

interface MasterDictionaryContextType {
    validCptList: DictCptEntry[];
    proceduresList: ApexProcedureItem[];
    codeListsByKey: Record<string, CodeListItem[]>;
    apexFolders: EcwDocFolder[];

    isCptValid: (cptCode: string) => boolean;
    getCptDesc: (cptCode: string) => string;
    getCptShortDesc: (cptCode: string) => string;
    getCodeList: (listKey: string) => CodeListItem[];

    refreshCodeLists: () => Promise<void>; // 🆕
    refreshSingleCodeList: (listKey: string) => Promise<void>; // 🆕
}

interface MasterDictionaryProviderProps {
    children: ReactNode;
}

const MasterDictionaryContext = createContext<MasterDictionaryContextType | undefined>(undefined);

export const useMasterDictionary = () => {
    const context = useContext(MasterDictionaryContext);
    if (!context) {
        throw new Error('useMasterDictionary must be used within a MasterDictionaryProvider');
    }
    return context;
};

export const MasterDictionaryProvider: React.FC<MasterDictionaryProviderProps> = ({ children }) => {
    const [validCptList, setValidCptList] = useState<DictCptEntry[]>([]);
    const [proceduresList, setProceduresList] = useState<ApexProcedureItem[]>([]);
    const [codeListsByKey, setCodeListsByKey] = useState<Record<string, CodeListItem[]>>({});
    const [apexFolders] = useState<EcwDocFolder[]>([]); // setApexFolders removed - not used

    const refreshCodeLists = useCallback(async () => {
        try {
            // Fetch distinct list keys first
            const keysResp = await axiosApexInstance.get('/task/lists/distinct-keys');
            const commonListKeys = keysResp.data; // This will be an array of distinct listKeys

            const codeLists: Record<string, CodeListItem[]> = {};

            for (const key of commonListKeys) {
                const resp = await axiosApexInstance.get(`/task/lists/bykey/${key}`);
                codeLists[key] = resp.data || [];
            }

            setCodeListsByKey(codeLists); // Store the result
        } catch (error) {
            console.error('Error refreshing code lists', error);
        }
    }, []);

    const fetchMasterData = useCallback(async () => {
        try {
            const [cptResp, procResp] = await Promise.all([
                axiosApexInstance.get('/task/claims/cptlist'),
                axiosApexInstance.get('/task/claims/proclist')
            ]);

            setValidCptList(cptResp.data || []);
            setProceduresList(
                (procResp.data || []).map((item: any) => ({
                    ...item,
                    cpts: item.cpts ? JSON.parse(item.cpts) as CPTItem[] : []
                }))
            );

            // load initial common lists
            await refreshCodeLists();

            // Debug logging removed for production
            // console.log("Fetched CPT list, procedure list, and common code lists.");
        } catch (error: any) {
            // Suppress error log if it's due to missing access token (pre-login case)
            if (error?.message === MISSING_TOKEN_ERROR_MSG ) {
                return;
            }

            console.error('Error fetching master data:', error);
        }
    }, [refreshCodeLists]);

    useEffect(() => {
        fetchMasterData();
    }, [fetchMasterData]);

    const refreshSingleCodeList = async (listKey: string) => {
        try {
            const resp = await axiosApexInstance.get(`/task/lists/bykey/${listKey}`);
            setCodeListsByKey(prev => ({
                ...prev,
                [listKey]: resp.data || [],
            }));
        } catch (error) {
            console.error(`Error refreshing code list ${listKey}`, error);
        }
    };

    const isCptValid = (cptCode: string): boolean => {
        return validCptList.some((cpt) => cpt.code === cptCode);
    };

    const getCptDesc = (cptCode: string): string => {
        const cpt = validCptList.find((c) => c.code === cptCode);
        return cpt ? cpt.description : '';
    };

    const getCptShortDesc = (cptCode: string): string => {
        const cpt = validCptList.find((c) => c.code === cptCode);
        if (cpt && cpt.description) {
            const shortDesc = cpt.description.length > 30 ? cpt.description.substring(0, 30) : cpt.description;
            return shortDesc;
        }
        return '';
    };

    const getCodeList = (listKey: string): CodeListItem[] => {
        return codeListsByKey[listKey] || [];
    };

    return (
        <MasterDictionaryContext.Provider
            value={{
                validCptList,
                proceduresList,
                codeListsByKey,
                apexFolders,
                isCptValid,
                getCptDesc,
                getCptShortDesc,
                getCodeList,
                refreshCodeLists,
                refreshSingleCodeList,
            }}
        >
            {children}
        </MasterDictionaryContext.Provider>
    );
};
